// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react'

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error'

interface UseAutosaveOptions<T> {
  data:        T
  onSave:      (data: T) => Promise<void>
  delay?:      number   // ms, default 600
  enabled?:    boolean  // default true
}

export function useAutosave<T>({
  data,
  onSave,
  delay   = 600,
  enabled = true,
}: UseAutosaveOptions<T>) {
  const [status, setStatus]     = useState<SaveStatus>('idle')
  const timerRef                = useRef<ReturnType<typeof setTimeout> | null>(null)
  const onSaveRef               = useRef(onSave)
  const isFirstRender           = useRef(true)
  // V40C-P1 fix: altijd de meest recente data beschikbaar voor saveNow
  // zonder deze ref kan saveNow stale closure-data opslaan bij directe navigatie
  const dataRef                 = useRef(data)

  // Keep refs current on every render
  useEffect(() => { onSaveRef.current = onSave }, [onSave])
  useLayoutEffect(() => { dataRef.current = data })   // useLayoutEffect: sync vóór paint - nooit stale

  useEffect(() => {
    // Skip autosave on initial mount
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    if (!enabled) return

    // Clear any pending timer
    if (timerRef.current) clearTimeout(timerRef.current)

    setStatus('saving')

    timerRef.current = setTimeout(async () => {
      try {
        await onSaveRef.current(dataRef.current)
        setStatus('saved')
        // Reset to idle after 2 seconds
        setTimeout(() => setStatus('idle'), 2000)
      } catch {
        setStatus('error')
      }
    }, delay)

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(data), delay, enabled])

  // saveNow leest altijd uit dataRef - niet uit stale closure
  const saveNow = useCallback(async () => {
    if (timerRef.current) clearTimeout(timerRef.current)
    setStatus('saving')
    try {
      await onSaveRef.current(dataRef.current)
      setStatus('saved')
      setTimeout(() => setStatus('idle'), 2000)
    } catch {
      setStatus('error')
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])   // geen deps nodig - dataRef.current is altijd actueel

  // syncData: roep dit aan na setData voor guaranteed-fresh dataRef
  const syncData = useCallback((d: T) => { dataRef.current = d }, [])

  return { status, saveNow, syncData }
}
